// Retailer-portal behaviour analytics (client).
//
// Queues events in memory and flushes batches to POST /api/portal/analytics/events
// (authed by the kos_portal cookie; retailer_id is stamped server-side). Page
// views are captured automatically from the router; pages call track() for the
// handful of high-value feature/action events.
//
// PII-free by contract: customData carries ids + labels only — never message
// bodies, names, or customer contact details. Best-effort and non-blocking:
// failures are swallowed, sends use sendBeacon / keepalive so they survive
// navigation.

import { API_BASE } from "@tkos/api-client";

export type PortalEventType = "page_view" | "feature_used" | "action";

export interface PortalEvent {
  eventType: PortalEventType;
  feature?: string;
  page?: string;
  /** Minimal, PII-free context — ids + short labels only. */
  customData?: Record<string, string | number | boolean>;
}

const ENDPOINT = `${API_BASE}/portal/analytics/events`;
const MAX_BATCH = 100;
const FLUSH_IDLE_MS = 2000;

let queue: PortalEvent[] = [];
let flushTimer: number | null = null;
let started = false;

/** Force-send queued events. Called on idle, batch-full, and page unload. */
export function flush(): void {
  if (flushTimer != null) { window.clearTimeout(flushTimer); flushTimer = null; }
  if (queue.length === 0) return;
  const events = queue;
  queue = [];
  const payload = JSON.stringify({ events });
  // Prefer sendBeacon — it survives the page unload that often follows a click.
  try {
    if (navigator.sendBeacon) {
      const blob = new Blob([payload], { type: "application/json" });
      if (navigator.sendBeacon(ENDPOINT, blob)) return;
    }
  } catch { /* fall through to fetch */ }
  try {
    void fetch(ENDPOINT, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
      credentials: "include",
    }).catch(() => { /* swallow — analytics never surface errors */ });
  } catch { /* ignore */ }
}

/** Enqueue an event. Auto-flushes when the batch fills or after a short idle. */
export function track(event: PortalEvent): void {
  if (!event || !event.eventType) return;
  queue.push(event);
  if (queue.length >= MAX_BATCH) { flush(); return; }
  if (flushTimer != null) window.clearTimeout(flushTimer);
  flushTimer = window.setTimeout(flush, FLUSH_IDLE_MS);
}

/** Convenience for the auto page-view hook. */
export function trackPageView(page: string): void {
  track({ eventType: "page_view", page });
}

/** Wire unload/visibility flush listeners once. Safe to call multiple times. */
export function initAnalytics(): void {
  if (started) return;
  started = true;
  window.addEventListener("pagehide", flush, { capture: true });
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush();
  });
}
